import React, { useState } from 'react';
import {
  X,
  CheckCircle,
  XCircle,
  Lock,
  Unlock,
  ShieldAlert,
  MapPin,
  DollarSign,
  Phone,
  Mail,
  User,
  Sparkles,
  Layers,
  Clock,
  Trash2,
  Tv,
  CheckCircle2,
  FileText,
} from 'lucide-react';
import CyberBadge from '../common/CyberBadge';
import { resolveAdminMediaUri } from '../../api';


export default function DrawerModal({
  drawerItem,
  drawerType,
  onClose,
  onApproveCaptain,
  onRejectCaptain,
  onBlockCaptain,
  onUnblockCaptain,
  onOpenUnlockModal,
  onApproveRoom,
  onRejectRoom,
  onDeleteRoom,
  onResolveReport,
}) {
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  if (!drawerItem) return null;

  // Extract images array or fallbacks based on room type
  const rawImages = drawerItem.images || (drawerItem.image ? [drawerItem.image] : []);
  const resolvedImages = rawImages.map(resolveAdminMediaUri).filter(Boolean);

  const defaultCategoryImages = {
    hotzone: [
      'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=800&q=80',
    ],
    studio: [
      'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=800&q=80',
    ],
    flat: [
      'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=800&q=80',
    ],
    gym: [
      'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1517838277536-f5f99be501cd?auto=format&fit=crop&w=800&q=80',
    ],
    ground_booking: [
      'https://images.unsplash.com/photo-1529900748604-07564a03e7a6?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=800&q=80',
    ],
  };

  const imagesList = resolvedImages.length > 0 ? resolvedImages : (
    defaultCategoryImages[drawerItem.categoryType] || defaultCategoryImages.hotzone
  );

  const videoUrl = drawerItem.videoUrl || (drawerItem.videos && drawerItem.videos[0]);
  const resolvedVideo = resolveAdminMediaUri(videoUrl);

  const realAmenities = Array.isArray(drawerItem.amenities) && drawerItem.amenities.length > 0
    ? drawerItem.amenities
    : (Array.isArray(drawerItem.moduleData?.amenities) && drawerItem.moduleData.amenities.length > 0
      ? drawerItem.moduleData.amenities
      : [
          `Category: ${(drawerItem.categoryType || 'General').toUpperCase()}`,
          `Module: ${(drawerItem.moduleType || 'Loka').toUpperCase()}`,
          `Pricing: ₹${drawerItem.price || 0} / ${drawerItem.priceType || 'unit'}`,
          `City Node: ${drawerItem.city || drawerItem.location?.city || 'Indore'}`,
          drawerItem.moduleData?.coupleEntryAllowed ? 'Couple Entry Allowed' : 'Standard Access Pass',
          drawerItem.moduleData?.eventTime ? `Time: ${drawerItem.moduleData.eventTime}` : '24/7 Verified Node'
        ]);

  return (
    <>
      <div className="right-drawer-overlay" onClick={onClose} />
      <div className="right-drawer" style={{ width: '580px' }}>
        {/* Drawer Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--bg-surface-elevated)',
          }}
        >
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--neon-cyan)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'var(--font-mono)' }}>
              {drawerType.toUpperCase()} DETAILS MATRIX
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 900, color: 'var(--text-primary)', margin: '2px 0 0 0' }}>
              {drawerItem.fullName || drawerItem.name || drawerItem.title || `Item #${drawerItem._id?.slice(-6)}`}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(0, 0, 0, 0.4)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
              borderRadius: '8px',
              width: '34px',
              height: '34px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Drawer Content Area */}
        <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '22px', overflowY: 'auto' }}>
          {/* ROOM / LISTING VIEW */}
          {drawerType === 'room' && (
            <>
              {/* Top Status & Action Bar */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CyberBadge status={drawerItem.status || 'approved'} />
                  <span className="badge badge-purple">{drawerItem.moduleType || 'loka'}</span>
                  <span className="badge badge-cyan">{drawerItem.categoryType || 'hotzone'}</span>
                </div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  ID: #{drawerItem._id}
                </span>
              </div>

              {/* Explicit Quick Action Approval Bar */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <button
                  className="btn btn-emerald"
                  onClick={async () => {
                    await onApproveRoom(drawerItem._id);
                    onClose();
                  }}
                  style={{ padding: '12px', fontSize: '0.88rem' }}
                >
                  <CheckCircle2 size={16} /> Approve Listing
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => setShowRejectForm(!showRejectForm)}
                  style={{ padding: '12px', fontSize: '0.88rem' }}
                >
                  <XCircle size={16} /> Reject Listing
                </button>
              </div>

              {showRejectForm && (
                <div className="cyber-card" style={{ padding: '16px', background: 'rgba(255, 51, 102, 0.1)', borderColor: 'rgba(255, 51, 102, 0.4)' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--neon-rose)', fontWeight: 700, display: 'block', marginBottom: '6px' }}>
                    Rejection Reason
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter reason for rejecting listing..."
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />
                  <button
                    className="btn btn-danger"
                    style={{ width: '100%' }}
                    onClick={async () => {
                      await onRejectRoom({ _id: drawerItem._id, reason: rejectNote });
                      onClose();
                    }}
                  >
                    Confirm Rejection
                  </button>
                </div>
              )}

              {/* Main Image Gallery */}
              <div>
                <h4 style={{ fontSize: '0.88rem', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '10px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>
                  📷 Listing Media Gallery ({imagesList.length})
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                  <div style={{ height: '200px', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                    <img
                      src={imagesList[0]}
                      alt="Primary"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=800&q=80';
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {imagesList.slice(1, 3).map((img, idx) => (
                      <div key={idx} style={{ height: '95px', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                        <img
                          src={img}
                          alt={`Gallery ${idx}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&w=800&q=80';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Video Stream Preview if available */}
              {resolvedVideo && (
                <div>
                  <h4 style={{ fontSize: '0.88rem', fontWeight: 800, color: 'var(--neon-cyan)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Tv size={16} /> Listing Video Preview Stream
                  </h4>
                  <video controls src={resolvedVideo} style={{ width: '100%', borderRadius: '10px', maxHeight: '220px', border: '1px solid var(--border-color)' }} />
                </div>
              )}

              {/* Primary Listing Details */}
              <div className="cyber-card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <span className="corner-tl" />
                <span className="corner-tr" />
                <span className="corner-bl" />
                <span className="corner-br" />

                <div style={{ fontSize: '1.2rem', fontWeight: 900, color: 'var(--text-primary)' }}>
                  {drawerItem.title}
                </div>

                <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {drawerItem.description || 'An underground trap beat factory and DJ club event venue in Vijay Nagar.'}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', paddingTop: '10px', borderTop: '1px solid var(--border-color)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DollarSign size={18} style={{ color: 'var(--neon-green)' }} />
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>PRICE RATE</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--neon-green)', fontFamily: 'var(--font-mono)' }}>
                        ₹{drawerItem.price || drawerItem.hourlyRate || 1000} / {drawerItem.priceType || 'hour'}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MapPin size={18} style={{ color: 'var(--neon-cyan)' }} />
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>LOCATION</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {drawerItem.address || 'Vijay Nagar Main Road'}, {drawerItem.city || 'Indore'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Host / Captain Information */}
              <div className="cyber-card" style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span className="corner-tl" />
                <span className="corner-tr" />
                <span className="corner-bl" />
                <span className="corner-br" />

                <h4 style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--neon-purple)', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <User size={15} /> Host & Captain Details
                </h4>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'rgba(168, 85, 247, 0.15)',
                      border: '1px solid rgba(168, 85, 247, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      color: 'var(--neon-purple)',
                    }}
                  >
                    CP
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                      {drawerItem.captain?.businessName || drawerItem.captain?.name || drawerItem.captainName || 'Admin / Direct Platform Partner'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {drawerItem.captain?.email || drawerItem.captainPhone || 'Verified Platform Node'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Verified Facilities & Amenities List */}
              <div className="cyber-card" style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span className="corner-tl" />
                <span className="corner-tr" />
                <span className="corner-bl" />
                <span className="corner-br" />

                <h4 style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--neon-green)', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Sparkles size={15} /> Verified Attributes & Specifications
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {realAmenities.map((am, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                      <CheckCircle2 size={13} style={{ color: 'var(--neon-green)' }} />
                      <span>{am}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Permanent Delete Action */}
              <div style={{ paddingTop: '10px', marginTop: 'auto', borderTop: '1px solid var(--border-color)' }}>
                <button
                  className="btn btn-danger"
                  style={{ width: '100%', padding: '12px', fontSize: '0.88rem' }}
                  onClick={async () => {
                    await onDeleteRoom(drawerItem._id);
                    onClose();
                  }}
                >
                  <Trash2 size={16} /> Delete Listing Permanently
                </button>
              </div>
            </>
          )}

          {/* CAPTAIN VIEW */}
          {drawerType === 'captain' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <CyberBadge status={drawerItem.status || 'pending'} />
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>ID: #{drawerItem._id}</span>
              </div>

              <div className="cyber-card" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85rem' }}>
                  <Mail size={15} style={{ color: 'var(--neon-cyan)' }} />
                  <span>{drawerItem.email || 'N/A'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85rem' }}>
                  <Phone size={15} style={{ color: 'var(--neon-cyan)' }} />
                  <span>{drawerItem.phone || 'N/A'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85rem' }}>
                  <MapPin size={15} style={{ color: 'var(--neon-cyan)' }} />
                  <span>{drawerItem.city || 'Indore'}</span>
                </div>
              </div>

              {/* Uploaded Verification Documents & ID */}
              <div className="cyber-card" style={{ padding: '16px' }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FileText size={15} style={{ color: 'var(--neon-cyan)' }} />
                  Verification Documents & ID ({drawerItem.documents?.length || 0})
                </h4>
                {drawerItem.documents && drawerItem.documents.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {drawerItem.documents.map((docUrl, idx) => (
                      <a
                        key={idx}
                        href={resolveAdminMediaUri(docUrl)}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 14px',
                          background: 'rgba(255, 255, 255, 0.04)',
                          border: '1px solid rgba(0, 240, 255, 0.25)',
                          borderRadius: '8px',
                          color: '#FFFFFF',
                          fontSize: '0.82rem',
                          textDecoration: 'none',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircle2 size={14} style={{ color: 'var(--neon-green)' }} />
                          Verification Document #{idx + 1}
                        </span>
                        <span style={{ color: 'var(--neon-cyan)', fontSize: '0.75rem', fontWeight: 700 }}>
                          View / Download &rarr;
                        </span>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No verification documents attached.</p>
                )}
              </div>

              {/* Unlocked Modules List */}
              <div className="cyber-card" style={{ padding: '16px' }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Unlock size={15} style={{ color: 'var(--neon-purple)' }} />
                  Unlocked Module Licenses
                </h4>
                {drawerItem.unlockedModules && drawerItem.unlockedModules.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {drawerItem.unlockedModules.map((m, idx) => (
                      <span key={idx} className="badge badge-purple" style={{ fontSize: '0.68rem' }}>
                        {typeof m === 'string' ? m : m.moduleName}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No modules currently unlocked.</p>
                )}
              </div>

              {/* Actions Footer */}
              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <button className="btn btn-emerald" onClick={() => onApproveCaptain(drawerItem._id)}>
                    <CheckCircle size={15} /> Approve Captain
                  </button>
                  <button className="btn btn-danger" onClick={() => onRejectCaptain(drawerItem)}>
                    <XCircle size={15} /> Reject Captain
                  </button>
                </div>

                <button className="btn btn-purple" onClick={() => onOpenUnlockModal(drawerItem)}>
                  <Unlock size={15} /> Grant Module Unlock License
                </button>

                {drawerItem.isBlocked ? (
                  <button className="btn btn-outline" onClick={() => onUnblockCaptain(drawerItem._id)}>
                    <CheckCircle size={15} /> Unblock Captain
                  </button>
                ) : (
                  <button className="btn btn-danger" onClick={() => onBlockCaptain(drawerItem._id)}>
                    <Lock size={15} /> Block Captain Account
                  </button>
                )}
              </div>
            </>
          )}

          {/* REPORT VIEW */}
          {drawerType === 'report' && (
            <>
              <div className="cyber-card" style={{ padding: '16px' }}>
                <h4 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--neon-rose)', marginBottom: '6px' }}>
                  <ShieldAlert size={16} style={{ display: 'inline', marginRight: '6px' }} />
                  {drawerItem.reason || 'User Reported Content'}
                </h4>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{drawerItem.details || 'No additional details provided.'}</p>
              </div>
              <button className="btn btn-emerald" onClick={() => onResolveReport(drawerItem._id, 'Resolved by Admin')}>
                <CheckCircle size={15} /> Mark Report Resolved
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
